import {
	ParcellingFormSection,
	NutrientTable,
	ParcellingTabs,
	ParcellingListCard,
	ExpandableFloraCard,
	ClientSelectionModal,
	FarmSelectionModal,
	FloraFillAnimation,
} from '../components';
import {
	Button,
	useToast,
	ConfirmModal,
	useConfirmModal,
	ExportDropdown,
	ActionButton,
} from '@practium/ui';
import { FileQuestion, Copy } from 'lucide-react';
import type { ParcellingPageConfig, BindingUsersResponse, PropDTOResponse } from '../types';
import { useParcellingPage } from '../hooks/useParcellingPage';
import { exportToExcel, exportToPdf } from '../utils';
import styles from './ParcellingPage.module.css';

export interface ParcellingPageProps {
	config: ParcellingPageConfig;
	page?: number;
	onPageChange?: (page: number) => void;
	fetchBindingUsers?: (query: string) => Promise<BindingUsersResponse>;
	fetchFarms?: (params?: { userId?: string; query?: string }) => Promise<PropDTOResponse>;
}

export const ParcellingPage = ({
	config,
	page,
	onPageChange,
	fetchBindingUsers,
	fetchFarms,
}: ParcellingPageProps) => {
	const toast = useToast();
	const {
		confirm,
		isOpen,
		options,
		handleConfirm,
		handleCancel: handleModalCancel,
	} = useConfirmModal();
	const {
		activeTab,
		handleTabChange,
		isEditing,
		permissions,
		parcellings,
		selectedParcellingId,
		selectedParcelling,
		formData,
		errors,
		isDirty,
		isSubmitting,
		updateParcels,
		handleSubmit,
		handleFloraClick,
		handleEdit,
		handleCancel,
		handleDelete,
		handleDuplicate,
		handleAddNew,
		handleSelectParcelling,
		handleFieldChange,
		hasData,
		selectedCanEdit,
		selectedCanDelete,
		canBind,
		canBindFarm,
		bindingUsers,
		bindingUserDisplayName,
		isAdmin,
		canManagePublicVisibility,
		clientModalOpen,
		farmModalOpen,
		handleOpenClientModal,
		handleCloseClientModal,
		handleClientSelect,
		handleOpenFarmModal,
		handleCloseFarmModal,
		handleFarmSelect,
		farms,
		selectedFarmIds,
		selectedFarmName,
		isFloraExpanded,
		isFloraAnimating,
		floraParcels,
		updateFloraParcels,
		handleToggleFloraExpand,
		handleFloraSendMessage,
	} = useParcellingPage({ config, toast, confirm, fetchBindingUsers, fetchFarms });

	const handleExportExcel = () => {
		if (selectedParcelling) exportToExcel(selectedParcelling);
	};

	const handleExportPdf = () => {
		if (selectedParcelling) exportToPdf(selectedParcelling);
	};

	return (
		<>
			<div className={styles.page}>
				<div className={styles.mainContent}>
					<div className={`${styles.card} ${isSubmitting ? styles.loading : ''}`}>
						{isSubmitting && (
							<div className={styles.loadingOverlay}>
								<div className={styles.loadingSpinner} />
							</div>
						)}
						<div className={styles.header}>
							<div className={styles.headerText}>
								<h1 className={styles.title}>Informações Gerais</h1>
								<p className={styles.subtitle}>
									Informações gerais sobre o parcelamento em questão
								</p>
							</div>
							<div className={styles.headerActions}>
								{permissions.canCreate && !isEditing && (
									<ActionButton text="Adicionar" onClick={handleAddNew} />
								)}
								{permissions.canEdit && !isEditing && selectedCanEdit && (
									<ActionButton text="Editar" onClick={handleEdit} />
								)}
								{permissions.canExport && hasData && (
									<ExportDropdown
										onExportExcel={handleExportExcel}
										onExportPdf={handleExportPdf}
									/>
								)}
								{permissions.canDuplicate && !isEditing && hasData && selectedParcelling && (
									<button
										type="button"
										className={styles.iconButton}
										onClick={handleDuplicate}
										title="Duplicar parcelamento"
										aria-label="Duplicar parcelamento"
									>
										<Copy className={styles.iconButtonIcon} />
									</button>
								)}
							</div>
						</div>

						{hasData ? (
							<>
								<ParcellingFormSection
									name={formData.name}
									culture={formData.culture}
									cropStage={formData.cropStage}
									isPublic={formData.isPublic}
									note={formData.note}
									bindingUserDisplayName={bindingUserDisplayName}
									selectedFarmName={selectedFarmName}
									canBindUser={canBind}
									canBindFarm={canBindFarm}
									canManagePublicVisibility={canManagePublicVisibility}
									onNameChange={handleFieldChange.name}
									onCultureChange={handleFieldChange.culture}
									onCropStageChange={handleFieldChange.cropStage}
									onIsPublicChange={handleFieldChange.isPublic}
									onNoteChange={handleFieldChange.note}
									onOpenClientModal={handleOpenClientModal}
									onOpenFarmModal={handleOpenFarmModal}
									isEditing={isEditing}
									errors={errors}
								/>

								<div className={styles.section}>
									<div className={styles.sectionHeader}>
										<h2 className={styles.title}>Dados do Parcelamento</h2>
										<h3 className={styles.subtitle}>
											Parâmetros do parcelamento relacionados aos elementos do solo
										</h3>
									</div>
									<ParcellingTabs
										activeTab={activeTab}
										onTabChange={handleTabChange}
										isEditing={isEditing}
									/>

									{activeTab === 'manual' ? (
										<NutrientTable
											data={formData.parcels}
											onChange={updateParcels}
											isEditing={isEditing}
										/>
									) : (
										<div className={styles.floraTabContent}>
											<NutrientTable
												data={floraParcels}
												onChange={updateFloraParcels}
												isEditing={isEditing}
											/>
											<FloraFillAnimation isAnimating={isFloraAnimating} />
										</div>
									)}
								</div>

								{isEditing && (
									<div className={styles.actions}>
										{isDirty && (
											<div className={styles.unsavedIndicator}>
												<span className={styles.unsavedDot} />
												<span>Alterações não salvas</span>
											</div>
										)}
										<div className={styles.actionButtons}>
											{permissions.canDelete && selectedCanDelete && !selectedParcelling?._id.startsWith('temp-') && (
												<Button
													variant="danger"
													className={styles.button}
													onClick={handleDelete}
													disabled={isSubmitting}
												>
													Excluir
												</Button>
											)}
											<Button
												variant="secondary"
												className={styles.button}
												onClick={handleCancel}
												disabled={isSubmitting}
											>
												Cancelar
											</Button>
											<Button
												variant="primary"
												className={styles.button}
												onClick={() => handleSubmit()}
												disabled={isSubmitting || !isDirty}
											>
												{isSubmitting ? 'Salvando...' : 'Salvar Parcelamento'}
											</Button>
										</div>
									</div>
								)}
							</>
						) : (
							<div className={styles.emptyState}>
								<div className={styles.emptyContent}>
									<FileQuestion className={styles.emptyIcon} />
									<p>Selecione um parcelamento ou adicione um novo</p>
								</div>
							</div>
						)}
					</div>
				</div>

				<aside className={styles.sidebar}>
					<div
						className={`${styles.listWrapper} ${isFloraExpanded ? styles.listWrapperShrunk : ''}`}
					>
						<ParcellingListCard
							parcellings={parcellings}
							selectedId={selectedParcellingId}
							onSelect={handleSelectParcelling}
							page={page ?? 1}
							totalPages={Math.max(1, config.mockData.totalPages || Math.ceil((config.mockData.totalDocs || parcellings.length) / 15))}
							onPageChange={onPageChange}
						/>
					</div>
					{isEditing && (
						<div
							className={`${styles.floraWrapper} ${isFloraExpanded ? styles.floraWrapperExpanded : ''}`}
						>
							<ExpandableFloraCard
								onFloraClick={handleFloraClick}
								onSendMessage={handleFloraSendMessage}
								isExpanded={isFloraExpanded}
								onToggleExpand={handleToggleFloraExpand}
							/>
						</div>
					)}
				</aside>
			</div>

			<ClientSelectionModal
				isOpen={clientModalOpen}
				onClose={handleCloseClientModal}
				onSave={handleClientSelect}
				users={bindingUsers}
				farms={farms}
				selectedUserIds={formData.bindingUserIds}
				showAllOption={isAdmin}
			/>

			<FarmSelectionModal
				isOpen={farmModalOpen}
				onClose={handleCloseFarmModal}
				onSave={handleFarmSelect}
				farms={farms}
				selectedFarmIds={selectedFarmIds}
				showAllOption={isAdmin}
			/>

			<ConfirmModal
				isOpen={isOpen}
				title={options.title}
				message={options.message}
				confirmText={options.confirmText}
				cancelText={options.cancelText}
				variant={options.variant}
				onConfirm={handleConfirm}
				onCancel={handleModalCancel}
			/>
		</>
	);
};
